import { InstagramLogo, TiktokLogo, AmazonLogo, Sparkle, WhatsappLogo, Lock } from "@phosphor-icons/react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { contacts } from "@/data/content";
import { scrollToId } from "@/components/Reveal";
import { socialKitMeta } from "@/data/socialKit";

export const Footer = () => {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const year = new Date().getFullYear();

  const kitLabel = socialKitMeta[lang].kicker;

  const goSection = (id) => {
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId(id), 200);
    } else {
      scrollToId(id);
    }
  };

  const links = [
    { id: "about", label: t.nav.about },
    { id: "why", label: t.nav.why },
    { id: "how", label: t.nav.how },
    { id: "format", label: t.nav.format },
    { id: "gallery", label: t.nav.gallery },
    { id: "investment", label: t.nav.investment },
    { id: "contact", label: t.nav.contact },
  ];

  const socials = [
    { key: "instagram", label: "Instagram", href: contacts.social.instagram, Icon: InstagramLogo },
    { key: "tiktok", label: "TikTok", href: contacts.social.tiktok, Icon: TiktokLogo },
    { key: "amazon", label: "Amazon Store", href: contacts.social.amazon, Icon: AmazonLogo },
  ];

  return (
    <footer data-testid="footer" className="relative bg-[#111111] text-[#FAFAF7]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 pt-20 pb-10">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3">
              <img src="/homezen-mark.png" alt="Home Zen" className="h-10 w-auto" />
              <span className="font-display text-3xl">HOME<span className="text-[#8B7355]">ZEN</span></span>
            </div>
            <p className="mt-4 text-white/50 max-w-xs leading-relaxed">{t.footer.tagline}</p>
            <p className="mt-6 text-[10px] tracking-luxe uppercase text-white/40 mb-3">{t.footer.social}</p>
            <div className="flex gap-3">
              {socials.map(({ key, label, href, Icon }) => (
                <a
                  key={key}
                  data-testid={`footer-social-${key}`}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  title={label}
                  className="h-10 w-10 border border-white/15 flex items-center justify-center hover:bg-[#8B7355] hover:border-[#8B7355] transition-colors"
                >
                  <Icon size={18} weight="light" />
                </a>
              ))}
            </div>
          </div>

          <div>
            <p className="text-xs tracking-luxe uppercase text-white/40 mb-5">{t.footer.explore}</p>
            <ul className="space-y-3">
              {links.map((l) => (
                <li key={l.id}>
                  <button onClick={() => goSection(l.id)} className="link-underline text-white/70 hover:text-white transition-colors">
                    {l.label}
                  </button>
                </li>
              ))}
              <li>
                <Link
                  to="/social-kit"
                  data-testid="footer-social-kit-link"
                  className="link-underline text-white/70 hover:text-white transition-colors inline-flex items-center gap-1.5"
                >
                  <Sparkle size={12} weight="fill" className="text-[#8B7355]" />
                  {kitLabel}
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <p className="text-xs tracking-luxe uppercase text-white/40 mb-5">{t.footer.contactTitle}</p>
            <ul className="space-y-3 text-white/70">
              <li><a href={`tel:${contacts.phoneTel}`} className="link-underline hover:text-white">{contacts.phoneDisplay}</a></li>
              <li>
                <a
                  data-testid="footer-whatsapp"
                  href={`https://wa.me/${contacts.whatsappNumber}?text=${encodeURIComponent(contacts.whatsappMessage[lang] || contacts.whatsappMessage.it)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="link-underline hover:text-white inline-flex items-center gap-1.5"
                >
                  <WhatsappLogo size={13} weight="fill" className="text-[#25D366]" />
                  WhatsApp
                </a>
              </li>
              <li><a href={`mailto:${contacts.email}`} className="link-underline hover:text-white">{contacts.email}</a></li>
              <li className="text-white/50">{contacts.address}</li>
            </ul>
          </div>

          <div>
            <p className="text-xs tracking-luxe uppercase text-white/40 mb-5">{t.footer.legal}</p>
            <ul className="space-y-3 text-white/70">
              <li><a href="#" className="link-underline hover:text-white">{t.footer.privacy}</a></li>
              <li><a href="#" className="link-underline hover:text-white">{t.footer.cookie}</a></li>
              <li><a href={contacts.brochure} download className="link-underline hover:text-white">{t.news.download.cta}</a></li>
              <li>
                <Link
                  to="/area-affiliati"
                  data-testid="footer-affiliates-link"
                  className="link-underline hover:text-white inline-flex items-center gap-1.5"
                >
                  <Lock size={11} weight="light" className="text-[#8B7355]" />
                  {t.affiliates.linkLabel}
                </Link>
              </li>
              <li className="text-white/50" data-testid="footer-vat">
                <span className="text-white/40">{t.footer.vatLabel}</span> {contacts.vat}
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between gap-4 text-xs text-white/40">
          <p>© {year} Home Zen Store. {t.footer.rights}</p>
          <p>{t.footer.vatLabel} {contacts.vat} — Eurostyle</p>
        </div>
      </div>
    </footer>
  );
};
